import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  FiHome, FiUsers, FiBarChart2, FiSettings, FiDollarSign,
  FiMenu, FiX, FiBell, FiChevronDown,
  FiLogOut, FiShield, FiUserCheck, FiChevronRight,
  FiCheckSquare, FiTrendingUp, FiFileText,
} from 'react-icons/fi';
import { MdOutlineLeaderboard } from 'react-icons/md';
import authService from '../services/authService';
import { can } from '../utils/permission';

const SIDEBAR_W   = 248;
const COLLAPSED_W = 64;

export default function Layout({ children, pageTitle = 'Dashboard' }) {
  const [collapsed,    setCollapsed]    = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileOpen,   setMobileOpen]  = useState(false);  // mobile drawer
  const [isMobile,     setIsMobile]    = useState(false);

  const navigate  = useNavigate();
  const location  = useLocation();

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const user        = authService.getUser();
  const displayName = authService.getUserDisplayName();
  const roleLabel   = authService.getUserRoleLabel();
  const initials    = displayName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || 'U';
  const W = isMobile ? 0 : (collapsed ? COLLAPSED_W : SIDEBAR_W);

  const handleLogout = () => {
    setShowUserMenu(false);
    if (window.confirm('Are you sure you want to logout?')) {
      authService.logout(navigate);
    }
  };

  // Check if current user is Admin
  const isAdminUser = (() => {
    try {
      const u = authService.getUser();
      if (!u) return false;
      const r = (roleLabel || '').toLowerCase().trim();
      return u?.role === null || u?.role === undefined
        || ['admin','administrator','super admin','superadmin'].includes(r);
    } catch { return false; }
  })();

  const menuSections = [
    {
      label: 'Core',
      items: [
        { path: '/dashboard', icon: <FiHome size={15} />, label: 'Dashboard' },
        can('lead_read') && { path: '/leads', icon: <MdOutlineLeaderboard size={15} />, label: 'Leads', perm: 'lead_read' },
      ].filter(Boolean),
    },
    {
      label: 'Administration',
      items: isAdminUser ? [
        { path: '/users', icon: <FiUsers size={15} />, label: 'Users', perm: 'user_read' },
        { path: '/roles', icon: <FiShield size={15} />, label: 'Roles & Permissions', perm: 'role_read' },
        { path: '/masters', icon: <FiSettings size={15} />, label: 'Masters', perm: 'product_read' },
      ].filter(Boolean) : [],
    },
    {
      label: 'Finance & MIS',
      items: [
        can('report_read') && { path: '/reports', icon: <FiBarChart2 size={15} />, label: 'Reports', perm: 'report_read' },
        can('report_read') && { path: '/incentive', icon: <FiDollarSign size={15} />, label: 'Incentive', perm: 'report_read' },
        can('lead_pdd_read') && { path: '/pdd-management', icon: <FiCheckSquare size={15} />, label: 'PDD Management' },
        can('lead_pdd_read') && { path: '/invoice-management', icon: <FiFileText size={15} />, label: 'Invoice Management' },
        can('lead_pdd_read') && { path: '/revenue-management', icon: <FiTrendingUp size={15} />, label: 'Revenue Management' },
      ].filter(Boolean),
    },
  ].filter(sec => sec.items.length > 0);

  /* ── Sidebar inner content (shared between desktop & mobile drawer) ── */
  const SidebarContent = ({ isDrawer = false }) => (
    <>
      {/* Header */}
      <div className="sidebar-header">
        <NavLink to="/dashboard" className="sidebar-logo" onClick={() => isDrawer && setMobileOpen(false)}>
          <div className="sidebar-logo-mark">
            <img src="/logo.jpeg" alt="CF" style={{ width: 26, height: 26, objectFit: 'contain', display: 'block' }} />
          </div>
          {(isDrawer || !collapsed) && (
            <div style={{ lineHeight: 1.3, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#0D1B2A', letterSpacing: '-0.02em' }}>Credi Flex</div>
              <div style={{ fontSize: 10, color: '#9AA5B4', fontWeight: 500, letterSpacing: '0.04em' }}>Consultants LLP</div>
            </div>
          )}
        </NavLink>
        {isDrawer ? (
          <button className="sidebar-toggle" onClick={() => setMobileOpen(false)}><FiX size={14} /></button>
        ) : (
          <button className="sidebar-toggle" onClick={() => setCollapsed(c => !c)}>
            {collapsed ? <FiMenu size={12} /> : <FiX size={12} />}
          </button>
        )}
      </div>

      {/* Nav links */}
      <div className="sidebar-nav">
        {menuSections.map((sec, si) => (
          <div key={si}>
            {(isDrawer || !collapsed) && sec.items.length > 1 && <div className="nav-section-label">{sec.label}</div>}
            {!isDrawer && collapsed && si > 0 && <div className="nav-divider" />}

            {sec.items
              .filter(item => isAdminUser || !item.perm || can(item.perm) === true)
              .map((item, ii) => (
                <NavLink
                  key={ii}
                  to={item.path}
                  title={(!isDrawer && collapsed) ? item.label : undefined}
                  className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                  style={{ justifyContent: (!isDrawer && collapsed) ? 'center' : undefined }}>
                  <span className="nav-icon" style={{ display: 'flex', flexShrink: 0, width: 16, justifyContent: 'center' }}>
                    {item.icon}
                  </span>
                  {(isDrawer || !collapsed) && (
                    <>
                      <span style={{ flex: 1, fontSize: 13 }}>{item.label}</span>
                      <FiChevronRight size={10} className="nav-chevron" style={{ flexShrink: 0, opacity: 0.7, color: '#E4E8EE' }} />
                    </>
                  )}
                </NavLink>
              ))}

            {si < menuSections.length - 1 && (isDrawer || !collapsed) && <div className="nav-divider" />}
          </div>
        ))}
      </div>

      {/* Bottom user card */}
      {(isDrawer || !collapsed) && (
        <div className="sidebar-user-card">
          <div className="user-avatar" style={{ width: 30, height: 30, fontSize: '11.5px' }}>{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0D1B2A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>{displayName}</div>
            <div style={{ fontSize: 10.5, color: '#9AA5B4' }}>{roleLabel || 'Super Admin'}</div>
          </div>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22C55E', flexShrink: 0, boxShadow: '0 0 0 2px #fff' }} />
        </div>
      )}
    </>
  );

  return (
    <>
      <style>{`
        /* ── Mobile drawer overlay ── */
        .mobile-overlay {
          display: none;
          position: fixed; inset: 0; z-index: 1199;
          background: rgba(0,0,0,0.45);
          backdrop-filter: blur(2px);
        }
        .mobile-overlay.open { display: block; }

        /* ── Mobile drawer ── */
        .mobile-drawer {
          position: fixed; top: 0; left: 0; bottom: 0;
          width: 260px; z-index: 1200;
          background: #fff;
          box-shadow: 4px 0 24px rgba(0,0,0,0.18);
          transform: translateX(-100%);
          transition: transform 0.25s cubic-bezier(0.16,1,0.3,1);
          display: flex; flex-direction: column;
          overflow-y: auto;
        }
        .mobile-drawer.open { transform: translateX(0); }

        /* ── Desktop sidebar ── */
        .sidebar {
          position: fixed; top: 0; left: 0; bottom: 0;
          display: flex; flex-direction: column;
          background: #fff;
          border-right: 1px solid #E4E8EE;
          box-shadow: 2px 0 8px rgba(13,27,42,0.06);
          overflow: hidden;
          transition: width 0.2s cubic-bezier(0.16,1,0.3,1);
          z-index: 100;
        }

        /* ── Hide desktop sidebar on mobile ── */
        @media (max-width: 767px) {
          .sidebar { display: none !important; }
          .topbar-hamburger { display: flex !important; }
          .main-area { margin-left: 0 !important; width: 100vw !important; max-width: 100vw !important; overflow-x: hidden !important; }
          .topbar { padding: 0 12px !important; }
          .topbar-search { display: none !important; }
          .topbar-title { font-size: 14px !important; }
          .content-area { padding: 12px 10px 24px !important; overflow-x: hidden !important; }
        }

        @media (min-width: 768px) {
          .topbar-hamburger { display: none !important; }
          .mobile-overlay, .mobile-drawer { display: none !important; }
        }

        /* ── Topbar hamburger (mobile only) ── */
        .topbar-hamburger {
          display: none;
          align-items: center; justify-content: center;
          width: 38px; height: 38px;
          border-radius: 9px;
          background: #F4F6F9;
          border: 1px solid #E4E8EE;
          cursor: pointer;
          color: #0D3B5E;
          flex-shrink: 0;
        }

        /* ── Topbar right: hide some items on small screens ── */
        @media (max-width: 480px) {
          .topbar-bell { display: none !important; }
          .user-pill-name { display: none !important; }
        }
      `}</style>

      {/* ── Mobile overlay ── */}
      <div className={`mobile-overlay${mobileOpen ? ' open' : ''}`} onClick={() => setMobileOpen(false)} />

      {/* ── Mobile drawer ── */}
      <div className={`mobile-drawer${mobileOpen ? ' open' : ''}`}>
        <SidebarContent isDrawer={true} />
      </div>

      <div className="app-shell">

        {/* ══════════════ DESKTOP SIDEBAR ══════════════ */}
        <nav className="sidebar" style={{ width: W }}>
          <SidebarContent isDrawer={false} />
        </nav>

        {/* ══════════════ MAIN AREA ══════════════ */}
        <div className="main-area" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: '100vh', marginLeft: W, transition: 'margin-left 0.2s', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>

          {/* Topbar */}
          <header className="topbar">

            {/* Hamburger (mobile only) */}
            <button className="topbar-hamburger" onClick={() => setMobileOpen(true)}>
              <FiMenu size={18} />
            </button>

            <h1 className="topbar-title" style={{ margin: 0, fontSize: 15.5, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1, color: '#0D1B2A', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {pageTitle}
            </h1>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {/* Bell */}
              <button className="topbar-icon-btn topbar-bell" style={{ position: 'relative' }}>
                <FiBell size={15} />
                <span style={{ position: 'absolute', top: 7, right: 7, width: 6, height: 6, borderRadius: '50%', background: '#DC2626', border: '1.5px solid #fff' }} />
              </button>

              {/* User pill */}
              <div style={{ position: 'relative' }}>
                <button className="user-pill" onClick={() => setShowUserMenu(v => !v)}>
                  <div className="user-avatar" style={{ width: 28, height: 28, fontSize: 11 }}>{initials}</div>
                  <div className="user-pill-name" style={{ lineHeight: 1.3 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0D1B2A' }}>{displayName}</div>
                    <div style={{ fontSize: 10.5, color: '#9AA5B4' }}>{roleLabel || 'Super Admin'}</div>
                  </div>
                  <FiChevronDown size={11} style={{ color: '#9AA5B4', flexShrink: 0, transition: 'transform 0.2s', transform: showUserMenu ? 'rotate(180deg)' : 'none' }} />
                </button>

                {showUserMenu && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setShowUserMenu(false)} />
                    <div className="dropdown-menu">
                      <div className="dropdown-header">
                        <div className="user-avatar" style={{ width: 36, height: 36, fontSize: 13, background: 'rgba(245,166,35,0.18)', border: '1.5px solid rgba(245,166,35,0.35)', color: '#FBBF4A' }}>{initials}</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
                        </div>
                      </div>
                      <div style={{ height: 1, background: '#E4E8EE' }} />
                      <button className="dropdown-item danger" onClick={handleLogout}>
                        <FiLogOut size={14} /> Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="content-area">
            {children}
          </main>
        </div>
      </div>
    </>
  );
}
